import type { Toast, ToastMessage } from 'primereact/toast';

/** The Toast methods the global toast exposes. */
export type GlobalToastHandle = Pick<Toast, 'show' | 'remove' | 'clear' | 'replace'>;

type StackEntry = { original: ToastMessage; shown: ToastMessage };

const toArray = (message: ToastMessage | ToastMessage[]) => (Array.isArray(message) ? message : [message]);

let nextToastId = 0;

/**
 * Keeps at most `max` toasts on screen: showing another closes the oldest first.
 * Pass `handleRemove` to the Toast's `onRemove`, so toasts that close themselves stop counting.
 */
export const createToastStackLimiter = (getToast: () => GlobalToastHandle | null, max = Infinity) => {
  // Oldest first. PrimeReact closes every toast that deep-equals the one removed, so each is shown
  // as a copy with its own id; otherwise closing one of two identical toasts would close both.
  let stack: StackEntry[] = [];

  const addToStack = (messages: ToastMessage[]) => {
    const entries = messages.map((original) => ({
      original,
      shown: { ...original, id: original.id ?? `global-toast-${nextToastId++}` },
    }));
    stack = [...stack, ...entries];
    return entries.map((entry) => entry.shown);
  };

  const remove = (message: ToastMessage | ToastMessage[]) => {
    const messages = toArray(message);
    const removed = stack.filter((entry) => messages.includes(entry.original) || messages.includes(entry.shown));
    if (removed.length === 0) return;

    stack = stack.filter((entry) => !removed.includes(entry));
    // One at a time: the Toast's remove is typed to take an array but ignores one.
    const toast = getToast();
    removed.forEach((entry) => toast?.remove(entry.shown));
  };

  const show = (message: ToastMessage | ToastMessage[]) => {
    const toast = getToast();
    if (!toast) return;

    const incoming = toArray(message).slice(-max);
    const overflow = stack.length + incoming.length - max;
    if (overflow > 0) remove(stack.slice(0, overflow).map((entry) => entry.shown));

    toast.show(addToStack(incoming));
  };

  const replace = (message: ToastMessage | ToastMessage[]) => {
    const toast = getToast();
    if (!toast) return;

    stack = [];
    toast.replace(addToStack(toArray(message).slice(-max)));
  };

  const clear = () => {
    stack = [];
    getToast()?.clear();
  };

  /** Called by the Toast when a message goes, whether timed out, closed with X, or removed. */
  const handleRemove = (message: ToastMessage) => {
    stack = stack.filter((entry) => entry.shown !== message);
  };

  const handle: GlobalToastHandle = { show, remove, replace, clear };

  return { handle, handleRemove };
};
